import { FileEntry, Flags } from '@ionic-native/file';
import { FileTransferObject } from '@ionic-native/file-transfer';
import { Observable } from 'rxjs';
import { fromPromise } from 'rxjs/observable/fromPromise';
import { of } from 'rxjs/observable/of';
import { _throw } from 'rxjs/observable/throw';
import { Subject } from 'rxjs/Subject';
import { FileSystemHelper } from '../../../app/helpers/file-system.helper';
import { CordovaFileHelper } from '../../../app/helpers/file-system/cordova-file.helper';
import { ShareRequestFile } from '../../services/share/file-request';
import { VasAppFacade } from '../../services/vas-app-facade.service';
import { File } from './file.model';

export class FileCordovaFacade {

    constructor(protected appFacade: VasAppFacade) {

    }

    /**
     * This method returns a FileEntry from the given File object.
     *
     * Note that the file may not exist in the app's storage
     *
     * @param file {File}
     * @param options {Flags}
     * @return {Promise<FileEntry>}
     */
    fileToFileEntry(file: File, options?: Flags): Promise<FileEntry> {
        return this.appFacade.filesystem.pathToFileEntry(
            file.localFileSystemPath,
            options
        );
    }

    /**
     * This method returns true if the file is located in the mobile app's disk
     * storage.
     *
     * @param file {File}
     * @return {Promise<boolean>} true if the file is found
     */
    fileExists(file: File): Observable<boolean> {

        if (!file.localFileSystemPath) {
            return of(false);
        }

        const nativeFile = this.appFacade.nativeFileService;
        const parsed = CordovaFileHelper.extractFilePathParts(
            file.localFileSystemPath
        );
        return fromPromise(
            nativeFile.checkFile(parsed.directoryPath, parsed.filename)
        );
    }

    /**
     * This method accepts an instance of File (VAS File model) and downloads
     * the content from the api directly to disk and returns an instance of
     * FileEntry.
     *
     * NOTE: This method should only be used in mobile app context
     *
     * The method is particularly suitable for large downloads where the
     * resulting data would crash the mobile app if not handled in this way.
     *
     * The file will be downloaded to the cache directory of the app.
     *
     * Once you have the FileEntry use FileFacade.cordova.openFile method to
     * invoke the FileOpener plugin which will allow the user to pick which app
     * to use to open the file on the phone.
     *
     * @param file {File}
     * @return {Observable<FileEntry>}
     */
    streamRemoteFileToDisk(file: File): Observable<FileEntry> {

        if (this.appFacade.platform.is('core')) {
            return _throw(new Error('method not available on desktop'));
        }

        const subject = new Subject<FileEntry>();
        const uri = this.appFacade.api.r(
            `/accounts/${file.account}/jobs/${file.job}/files/${file.id}/content`
        );
        const fileTransfer: FileTransferObject = this.appFacade.fileTransferService.create();

        fileTransfer.download(
            uri,
            `${this.appFacade.nativeFileService.cacheDirectory}/${file.id}`,
            false,
            {
                headers: {
                    'Authorization': `Bearer ${this.appFacade.api.token}`
                }
            }
        ).then(
            (entry: FileEntry) => {
                subject.next(entry);
                subject.complete();
            },
            error => {
                subject.error(error);
            }
        );

        return subject;
    }

    /**
     * This method accepts a local file path and mime type and uses these with
     * the file opener plugin to display the open file dialog to the user on
     * their mobile device.
     *
     * NOTE: This method is only supported on mobile app
     *
     * @param path {string}
     * @param mimeType {string}
     * @return {Observable<any>}
     */
    openFile(path: string, mimeType: string): Observable<any> {

        if (this.appFacade.platform.is('core')) {
            return _throw(Error('method not supported on desktop'));
        }

        return fromPromise(
            this.appFacade.fileOpenerService.open(path, mimeType)
        );
    }

    /**
     * Opens the file sharing dialog with the file pointed to by the url.
     *
     * The url can be either a dataUrl, path to local file.
     *
     * NOTE: This method is only supported on mobile app
     *
     * @param url {string}
     * @return {Promise<any>}
     */
    async shareFile(url: string): Promise<any> {

        if (this.appFacade.platform.is('core')) {
            return Promise.reject(Error('method not supported on desktop'));
        }

        const request = new ShareRequestFile({
            attachments: [url]
        });

        return this.appFacade.shareService.share(request);
    }

    async fileToBlob(file: File): Promise<Blob> {

        const fileEntry = await this.fileToFileEntry(file);

        return new Promise<Blob>(((resolve, reject) => {
            fileEntry.file(
                iFile => {
                    resolve(CordovaFileHelper.cordovaFileToBlob(iFile));
                },
                reject
            );
        }));
    }

    async fileToDataUrl(file: File): Promise<string> {
        const blob = await this.fileToBlob(file);
        return FileSystemHelper.blobToDataUrl(blob);
    }
}



// WEBPACK FOOTER //
// ./src/vas/business-objects/file/file-cordova.facade.ts